import {
  getPathFromState as defaultGetPathFromState,
  getStateFromPath as defaultGetStateFromPath,
} from '@react-navigation/native';
import type { LinkingOptions } from '@react-navigation/native';

import type { RootStackParamList } from './types';
import {
  isDeleteAccountPath,
  isJoinPath,
  isSharePath,
  rewriteLegacyWebPath,
  WEB_DELETE_ACCOUNT_PATH,
  WEB_SETTINGS_PATH,
  WEB_THANKS_PATH,
} from './webPaths';

function appHomeState() {
  return {
    routes: [
      {
        name: 'MainTabs' as const,
        state: {
          routes: [{ name: 'Home' as const }],
          index: 0,
        },
      },
    ],
    index: 0,
  };
}

/**
 * Browser URL map.
 *
 *   /                      → marketing landing
 *   /privacy  /terms  /support → legal / help
 *   /thanks                  → post-purchase thank you
 *   /app/delete-account    → signed-in account deactivation (auth overlay)
 *   /app/settings          → Settings (subscription, household, delete account)
 *   /delete-account        → rewrite to /app/delete-account
 *   /explore               → sample dashboard (opt-in)
 *   /app  /app/home        → Home tab (signed-in product)
 *   /app/spend             → Spend
 *   /app/spend/transactions → Transaction list
 *   /app/wealth            → Wealth
 *   /app/grow              → Grow
 *   /app/loan              → Loan
 *   /app/join?token=…      → Home (JoinInviteListener)
 *
 * Legacy bookmarks (`/home`, `/spend`, `/preview`, `/join`, …) rewrite to `/app/…`.
 */
export const webLinking: LinkingOptions<RootStackParamList> = {
  prefixes: [
    typeof window !== 'undefined' && window.location?.origin
      ? window.location.origin
      : 'http://localhost:3000',
  ],
  config: {
    screens: {
      DeleteAccount: 'app/delete-account',
      Settings: 'app/settings',
      MainTabs: {
        path: 'app',
        screens: {
          Home: 'home',
          Spend: {
            path: 'spend',
            screens: {
              SpendHome: '',
              TransactionList: 'transactions',
            },
          },
          Wealth: 'wealth',
          Grow: 'grow',
          Loan: 'loan',
        },
      },
      Explore: {
        path: 'explore',
        screens: {
          Home: '',
          Spend: {
            path: 'spend',
            screens: {
              SpendHome: '',
              TransactionList: 'transactions',
            },
          },
          Wealth: 'wealth',
          Grow: 'grow',
          Loan: 'loan',
        },
      },
      Privacy: 'privacy',
      Terms: 'terms',
      Support: 'support',
      Thanks: 'thanks',
      Landing: '',
    },
  },
  getStateFromPath: (path, options) => {
    const withoutHash = path.split('#')[0] ?? path;
    const rewritten = rewriteLegacyWebPath(withoutHash);
    const pathname = (rewritten.split('?')[0] ?? '/').replace(/\/+$/, '') || '/';

    if (pathname === '/') {
      return { routes: [{ name: 'Landing' }], index: 0 };
    }
    if (pathname === '/privacy') {
      return { routes: [{ name: 'Privacy' }], index: 0 };
    }
    if (pathname === '/terms') {
      return { routes: [{ name: 'Terms' }], index: 0 };
    }
    if (pathname === '/support') {
      return { routes: [{ name: 'Support' }], index: 0 };
    }
    if (pathname === WEB_THANKS_PATH) {
      return { routes: [{ name: 'Thanks' }], index: 0 };
    }
    if (isDeleteAccountPath(pathname)) {
      return { routes: [{ name: 'DeleteAccount' }], index: 0 };
    }
    if (pathname === WEB_SETTINGS_PATH) {
      return { routes: [{ name: 'Settings' }], index: 0 };
    }
    if (isJoinPath(pathname) || isSharePath(pathname)) {
      return appHomeState();
    }

    return defaultGetStateFromPath(rewritten, options);
  },
  getPathFromState: (state, options) => {
    const route = state.routes[state.index];
    if (route?.name === 'DeleteAccount') {
      return WEB_DELETE_ACCOUNT_PATH;
    }
    if (route?.name === 'Settings') {
      return WEB_SETTINGS_PATH;
    }
    const path = defaultGetPathFromState(state, options);
    if (route?.name === 'Landing' && typeof window !== 'undefined') {
      const hash = window.location.hash;
      if (/^#(product|how|app|pricing)$/.test(hash)) {
        return `/${hash}`;
      }
    }
    return path;
  },
};
